import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BadgeCheck, Building2, RefreshCw, Stethoscope } from 'lucide-react';
import { Alert, Button, TextInput } from '@parsina/framework';
import { useClinicalTranslation } from '@parsina/framework/clinical';
import { C, soft } from '@parsina/framework/tokens';

import { useTranslation, LTR_TEXT } from '../../i18n';
import { PrimaryAction } from '../../components/auth/OnboardingParts.jsx';
import {
  completeCouncilStep, councilStepError, useCouncilLookup,
} from '../Onboarding/councilLookup.js';
import {
  TRACK, officeNameFor, placeFromRegistry, writeQuickPlan,
} from '../Onboarding/quickSetup.js';

/**
 * «خوش آمدید!» — an Iranian account's whole registration, on «شروع».
 *
 * One field. The کد نظام پزشکی is looked up by itself as it is typed
 * (councilLookup.js), the council's record is shown with the clinic name it
 * will produce, and one press writes the practitioner and hands over to the
 * run that builds everything else (quickSetup.js). Nothing else is asked:
 * phone, address and the rest are «کلینیک من»'s, whenever the doctor wants.
 *
 * «پزشک نیستم» sits under the field the way «رمز عبور را فراموش کرده‌اید؟»
 * sits under a password — present, small, and not a competing button. It opens
 * the one-field clinic form.
 *
 * Mounted inside `<ClinicalI18n>`: the lookup's words (its errors, «در حال
 * استعلام…», the field label) are the clinical catalogue's, shared with the
 * doctor panel; the rest of the copy is this panel's.
 */
export default function DoctorWelcome({ disabled = false }) {
  const { t } = useTranslation();
  const { t: ct } = useClinicalTranslation();
  const navigate = useNavigate();
  const lookup = useCouncilLookup(ct);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState(null);

  const { member } = lookup;
  const clinicName = member ? officeNameFor(member.fullName) : '';
  const error = saveError || lookup.error;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!member || saving || disabled) return;
    setSaving(true);
    setSaveError(null);
    try {
      const doctor = await completeCouncilStep(member);
      if (!doctor?.id) throw new Error(ct('errors.save.doctorProfile'));
      writeQuickPlan({
        track: TRACK.DOCTOR,
        clinicName,
        doctorName: member.fullName || '',
        specialty: member.specialty || '',
        ...placeFromRegistry(member),
      });
      navigate('/onboarding?quick=run');
    } catch (err) {
      setSaveError(councilStepError(err, ct));
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="start-doctor-form">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span
          aria-hidden="true"
          style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0, background: soft(C.navy, 0.1),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Stethoscope size={22} color={C.navy} />
        </span>
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: C.textPrimary }}>
          {t('clinic.start.welcome')}
        </h2>
      </div>
      <p style={{ margin: '10px 0 20px', fontSize: '0.8125rem', lineHeight: 1.9, color: C.textSecondary }}>
        {t('clinic.start.welcomeBody')}
      </p>

      <TextInput
        label={ct('common.labels.medicalCode')}
        required
        value={lookup.code}
        onChange={(e) => { setSaveError(null); lookup.onChange(e.target.value); }}
        onBlur={lookup.onBlur}
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        autoFocus
        dir={LTR_TEXT.dir}
        inputStyle={LTR_TEXT.style}
        disabled={saving || disabled}
        data-testid="start-medical-code"
      />

      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginTop: 6,
      }}
      >
        <div
          aria-live="polite"
          data-testid="start-lookup-status"
          style={{ flex: 1, minHeight: 18, fontSize: '0.75rem', lineHeight: 1.7, color: C.textSecondary }}
        >
          {lookup.looking ? ct('auth.onboarding.lookingUp') : (member ? '' : t('clinic.start.autoLookup'))}
        </div>
        <Button
          variant="link"
          type="button"
          onClick={() => navigate('/onboarding?quick=1')}
          disabled={saving}
          data-testid="start-not-doctor"
          style={{ height: 'auto', padding: 0, fontWeight: 700 }}
        >
          {t('clinic.start.notDoctor')}
        </Button>
      </div>

      {error ? (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* The registry being down is OUR failure, and says so in amber —
              never the red of «this number is wrong», which would send a real
              doctor away with nothing left to try. */}
          <Alert variant={lookup.retryable && !saveError ? 'warning' : 'error'} data-testid="start-doctor-error">
            {error}
          </Alert>
          {lookup.retryable && !saveError ? (
            <Button
              variant="outline"
              type="button"
              onClick={lookup.retry}
              disabled={lookup.looking}
              data-testid="start-lookup-retry"
              style={{ alignSelf: 'flex-start' }}
            >
              <RefreshCw size={14} aria-hidden="true" />
              {t('clinic.start.retryLookup')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {member ? (
        <div
          data-testid="start-council-record"
          style={{
            marginTop: 14, borderRadius: 12, padding: '14px 16px',
            border: `1px solid ${soft(C.teal, 0.3)}`, background: soft(C.teal, 0.06),
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <BadgeCheck size={22} color={C.teal} aria-hidden="true" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.9375rem', fontWeight: 800, color: C.textPrimary, lineHeight: 1.6 }}>
                {member.fullName}
              </div>
              <div style={{ fontSize: '0.78125rem', color: C.textSecondary, lineHeight: 1.7 }}>
                {[member.specialty, member.city].filter(Boolean).join(' — ')}
              </div>
            </div>
          </div>
          <div style={{ height: 1, background: soft(C.teal, 0.2), margin: '12px 0 10px' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8125rem' }}>
            <Building2 size={16} color={C.textSecondary} aria-hidden="true" style={{ flexShrink: 0 }} />
            <span style={{ color: C.textSecondary }}>{t('clinic.start.yourClinic')}</span>
            <strong data-testid="start-clinic-name" style={{ color: C.textPrimary, fontWeight: 800 }}>{clinicName}</strong>
          </div>
          <div style={{ fontSize: '0.75rem', color: C.textSecondary, marginTop: 6, lineHeight: 1.8 }}>
            {t('clinic.start.completeLater')}
          </div>
        </div>
      ) : null}

      <PrimaryAction
        fullWidth
        type="submit"
        disabled={!member || saving || disabled}
        data-testid="start-confirm"
        style={{ marginTop: 18 }}
      >
        {saving ? t('common.state.saving') : t('clinic.start.confirmEnter')}
      </PrimaryAction>
    </form>
  );
}
